import React, { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import { serviceProAccess, homeowner } from '../utils/role';
import ConfirmLayout from '../layouts/confirm';
import NewUserLayout from '../layouts/newUser';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------
// Route-level code splitting: each page is loaded in its own chunk on demand
// so the initial bundle no longer ships every admin / service-pro / homeowner
// page up front. `Loadable` wraps a lazy component in a Suspense boundary.

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// A DEPLOY USED TO WHITE-SCREEN ANY TAB THAT WAS ALREADY OPEN.
//
// Every page above is a separate chunk fetched on demand, and the chunk names are
// content-hashed. A tab that booted before a deploy is holding the OLD index.html,
// so the first route it lazy-loads afterwards asks for a hash that no longer
// exists. firebase.json rewrites `**` to /index.html, so that request returns HTML
// rather than a 404 — the browser parses it as JavaScript and throws
// `SyntaxError: Unexpected token '<'`. The Suspense boundary has no error handler,
// so React unmounts to a blank page. Observed live on 2026-08-31.
//
// Reloading picks up the new index.html and its current hashes. Guarded by a
// sessionStorage flag so a genuine, repeatable import error surfaces as an error
// instead of an infinite reload loop; the flag is cleared by the next chunk that
// loads successfully, so a LATER deploy can still recover the same way.
//
// Deliberately catches every import failure rather than sniffing the message:
// the signature differs across browsers ('ChunkLoadError', 'Loading chunk N
// failed', 'Unexpected token', 'Failed to fetch dynamically imported module') and
// the once-guard already bounds the cost of guessing wrong to one reload.
const CHUNK_RELOAD_KEY = 'resiview:chunk-reloaded';

const lazyWithReload = (factory) =>
  lazy(() =>
    factory()
      .then((module) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        } catch (err) {
          // Private mode / blocked storage. Nothing to recover, carry on.
        }
        return module;
      })
      .catch((error) => {
        let reloadedAlready = true;
        try {
          reloadedAlready = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
          if (!reloadedAlready) sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        } catch (err) {
          // Storage unavailable: fail loudly rather than risk a reload loop.
          throw error;
        }
        if (reloadedAlready) throw error;
        window.location.reload();
        // Never settles — the reload replaces the page before React sees this.
        return new Promise(() => {});
      })
  );

const Login = Loadable(lazyWithReload(() => import('../pages/authentication/Login')));
const Walkthrough = Loadable(lazyWithReload(() => import('../pages/dashboard/walkthrough/Walkthrough')));
const CareSignup = Loadable(lazyWithReload(() => import('../pages/dashboard/careSignup/CareSignup')));
const QuickAdd = Loadable(lazyWithReload(() => import('../pages/dashboard/QuickAdd')));
const Register = Loadable(lazyWithReload(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazyWithReload(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazyWithReload(() => import('../pages/authentication/VerifyCode')));
const Residance = Loadable(lazyWithReload(() => import('../pages/dashboard/Residence')));
const MoveWorkflow = Loadable(lazyWithReload(() => import('../pages/dashboard/MoveWorkflow')));
const Contacts = Loadable(lazyWithReload(() => import('../pages/dashboard/Contact')));
const MyServicePros = Loadable(lazyWithReload(() => import('../pages/dashboard/MyServicePros')));
const ContactCreate = Loadable(lazyWithReload(() => import('../pages/dashboard/ContactCreate')));
const Tasks = Loadable(lazyWithReload(() => import('../pages/dashboard/Tasks')));
const Budget = Loadable(lazyWithReload(() => import('../pages/dashboard/Budget')));
const Binder = Loadable(lazyWithReload(() => import('../pages/dashboard/Binder')));
const HomeHealthReport = Loadable(lazyWithReload(() => import('../pages/dashboard/HomeHealthReport')));
const SharedHomeHealthReport = Loadable(lazyWithReload(() => import('../pages/SharedHomeHealthReport')));
const CompanyProfile = Loadable(lazyWithReload(() => import('../pages/CompanyProfile')));
const Warranty = Loadable(lazyWithReload(() => import('../pages/dashboard/Warranty')));
const WarrantyCreate = Loadable(lazyWithReload(() => import('../pages/dashboard/WarrantyCreate')));
const Services = Loadable(lazyWithReload(() => import('../pages/dashboard/Services')));
const ServiceCreate = Loadable(lazyWithReload(() => import('../pages/dashboard/ServiceCreate')));
const BlogPosts = Loadable(lazyWithReload(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazyWithReload(() => import('../pages/dashboard/BlogPost')));
const BlogAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/BlogAction')));
const NewsAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/NewsAction')));
const ComingSoonPostAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/ComingSoonPostAction')));
const ItemAdmin = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/ItemAdmin')));
const CareFulfillment = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/CareFulfillment')));
const ProClaimReview = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/ProClaimReview')));
const ProFunnel = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/ProFunnel')));
const UserProfile = Loadable(lazyWithReload(() => import('../pages/dashboard/UserProfile')));
const UserAccount = Loadable(lazyWithReload(() => import('../pages/dashboard/UserAccount')));
const RoleAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/RoleAction')));
const MasterApplianceTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterApplianceTypeAction'))
);
const MasterIndustryTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterIndustryTypeAction'))
);
const MasterExteriorTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterExteriorTypeAction'))
);
const MasterLawnTypeAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/MasterLawnTypeAction')));
const MasterSendEmailNotificationAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterSendEmailNotificationAction'))
);
const MasterFurnitureTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterFurnitureTypeAction'))
);
const MasterInteriorTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterInteriorTypeAction'))
);
const MasterUtilitiesTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/admin/MasterUtilitiesTypeAction'))
);
const MasterBrandAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/MasterBrandAction')));
const HomeHacksAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/HomeHacksAction')));
const BroadcastAction = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/BroadcastAction')));
const MasterCompanyTypeAction = Loadable(
  lazyWithReload(() => import('../pages/dashboard/service-pro/MasterCompanyTypeAction'))
);
const CompanyAction = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CompanyAction')));
const AdminCompanyAction = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/AdminCompanyAction')));
const UserAction = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/UserAction')));
const AddressSearch = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/AddressSearch')));
const ServiceProDashboard = Loadable(
  lazyWithReload(() => import('../pages/dashboard/service-pro/ServiceProDashboard'))
);
const DispatchBoard = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/DispatchBoard')));
const CareVisit = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CareVisit')));
const ServicePlans = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/ServicePlans')));
const Territories = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/Territories')));
const Trial = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/Trial')));
const ProInvoices = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/Invoices')));
const CrmIntegrations = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CrmIntegrations')));
const CrmCallback = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CrmCallback')));
const Invoices = Loadable(lazyWithReload(() => import('../pages/dashboard/Invoices')));
const Marketing = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/Marketing')));
const Campaigns = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/Campaigns')));
const CustomerAction = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CustomerAction')));
const CustomersMap = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/CustomersMap')));
const InspectionAction = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/InspectionAction')));
const InspectionDetail = Loadable(lazyWithReload(() => import('../pages/dashboard/service-pro/InspectionDetail')));
const LandingPage = Loadable(lazyWithReload(() => import('../pages/LandingPage')));
const Care = Loadable(lazyWithReload(() => import('../pages/Care')));
const ProAudit = Loadable(lazyWithReload(() => import('../pages/ProAudit')));
const ProAuditReport = Loadable(lazyWithReload(() => import('../pages/ProAuditReport')));
const ProAuditUnsubscribe = Loadable(lazyWithReload(() => import('../pages/ProAuditUnsubscribe')));
const ProAuditClaim = Loadable(lazyWithReload(() => import('../pages/ProAuditClaim')));
const About = Loadable(lazyWithReload(() => import('../pages/About')));
const Faqs = Loadable(lazyWithReload(() => import('../pages/Faqs')));
const Terms = Loadable(lazyWithReload(() => import('../pages/Terms')));
const Privacy = Loadable(lazyWithReload(() => import('../pages/Privacy')));
const ComingSoon = Loadable(lazyWithReload(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazyWithReload(() => import('../pages/Maintenance')));
const Unsubscribe = Loadable(lazyWithReload(() => import('../pages/Unsubscribe')));
const CampaignUnsubscribe = Loadable(lazyWithReload(() => import('../pages/CampaignUnsubscribe')));
const Page500 = Loadable(lazyWithReload(() => import('../pages/Page500')));
const NotFound = Loadable(lazyWithReload(() => import('../pages/Page404')));
const EditItem = Loadable(lazyWithReload(() => import('../pages/dashboard/admin/EditItem')));
const DashboardAdminItemsItemIdTasksNew = Loadable(
  lazyWithReload(() => import('../pages/DashboardAdminItemsItemIdTasksNew'))
);
const DashboardHome = Loadable(lazyWithReload(() => import('../pages/DashboardHome')));
const DashboardHomeItems = Loadable(lazyWithReload(() => import('../pages/DashboardHomeItems')));
const DashboardHomeItemsId = Loadable(lazyWithReload(() => import('../pages/DashboardHomeItemDetail')));

const onlyAdminAccess = ['Admin'];

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'home',
          element: (
            <GuestGuard>
              <About />
            </GuestGuard>
          )
        },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },
    // Card 197: the recipient of a shared Home Health Report has no account.
    // Unauthenticated on purpose — the signed token is the credential.
    { path: 'report/:token', element: <SharedHomeHealthReport /> },
    // Card 45: a company's public profile. No guard — it is the landing target
    // for "View profile" and (once card 42 lands) for ads, so it has to render
    // for a visitor with no ResiView account.
    { path: 'pro/:companyId', element: <CompanyProfile /> },
    {
      path: 'confirm/:token',
      element: <ConfirmLayout />
    },
    {
      path: 'getToNewUser/:token',
      element: <NewUserLayout />
    },

    // Blog Routes
    {
      path: 'blog',
      children: [
        { path: '/blog', element: <Navigate to="/blog/posts" replace /> },
        { path: 'posts', element: <BlogPosts /> },
        { path: 'posts/:title', element: <BlogPost /> }
      ]
    },

    // News Routes
    {
      path: 'news',
      children: [
        { path: '/news', element: <Navigate to="/news/posts" replace /> },
        { path: 'posts', element: <BlogPosts /> },
        { path: 'posts/:title', element: <BlogPost /> }
      ]
    },

    // Coming Soon Post Routes
    {
      path: 'coming-soon',
      children: [
        { path: '/coming-soon', element: <Navigate to="/coming-soon/posts" replace /> },
        { path: 'posts', element: <BlogPosts /> },
        { path: 'posts/:title', element: <BlogPost /> }
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '/dashboard', element: <Navigate to="dashboard/home" replace /> },
        {
          // The address page was the Residence page before it grew tabs — the
          // same AddressNewForm, on its own, with nothing linking to it. Kept as
          // a redirect (the card-206 precedent) so any old bookmark lands on
          // the page that replaced it rather than on a second, divergent copy.
          path: 'address',
          element: <Navigate to="/dashboard/residence" replace />
        },
        {
          // Card 206: adding an item is a dialog over the grid, not its own
          // page. The route is kept so existing links still land somewhere
          // sensible — the grid renders with the add dialog already open.
          path: 'home-items/new',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <DashboardHomeItems />
            </RoleBasedGuard>
          )
        },
        {
          path: 'home-items/:id',
          element: <DashboardHomeItemsId />
        },
        {
          path: 'residence',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Residance />
            </RoleBasedGuard>
          )
        },
        {
          path: 'move',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <MoveWorkflow />
            </RoleBasedGuard>
          )
        },
        {
          path: 'walkthrough',
          element: <Walkthrough />
        },
        {
          // Home Care signup (card 219). A SEPARATE funnel from the walkthrough
          // above, which stays three light steps — someone who arrived from a
          // box ad has a different job than someone setting up an account, and
          // bolting these screens onto the walkthrough would make the fast path
          // slow for everyone who did not come from that ad.
          path: 'care-signup',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <CareSignup />
            </RoleBasedGuard>
          )
        },
        {
          path: 'quick-add',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <QuickAdd />
            </RoleBasedGuard>
          )
        },
        {
          path: 'home-items',
          children: [
            {
              path: '/dashboard/home-items',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <DashboardHomeItems />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'my-service-pros',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <MyServicePros />
            </RoleBasedGuard>
          )
        },
        // Invoicing & payment (card 48). One component serves the list, a single
        // invoice, and the return trip from the payment processor — the /paid
        // leg confirms with the SERVER rather than trusting the redirect.
        {
          path: 'invoices',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Invoices />
            </RoleBasedGuard>
          )
        },
        {
          path: 'invoices/:id',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Invoices />
            </RoleBasedGuard>
          )
        },
        {
          path: 'invoices/:id/paid',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Invoices />
            </RoleBasedGuard>
          )
        },
        {
          path: 'contacts',
          children: [
            {
              path: '/dashboard/contacts',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Contacts />{' '}
                </RoleBasedGuard>
              )
            },
            {
              // Card 207: adding a contact is a dialog over the list, but this
              // path still resolves — My Service Pros links straight to it.
              path: 'create',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Contacts />{' '}
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <ContactCreate />{' '}
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'warranty',
          children: [
            {
              path: '/dashboard/warranty',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Warranty />{' '}
                </RoleBasedGuard>
              )
            },
            {
              // Card 207: adding a warranty is a dialog over the list; this path
              // still resolves so old bookmarks land somewhere useful.
              path: 'create',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Warranty />{' '}
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <WarrantyCreate />{' '}
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'services',
          children: [
            {
              path: '/dashboard/services',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Services />
                </RoleBasedGuard>
              )
            },
            {
              path: 'create',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <ServiceCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <ServiceCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'home',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <DashboardHome />
            </RoleBasedGuard>
          )
        },
        {
          path: 'budget',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Budget />
            </RoleBasedGuard>
          )
        },
        {
          path: 'binder',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <Binder />
            </RoleBasedGuard>
          )
        },
        {
          path: 'home-health-report',
          element: (
            <RoleBasedGuard accessibleRoles={homeowner()}>
              <HomeHealthReport />
            </RoleBasedGuard>
          )
        },
        {
          path: 'tasks',
          children: [
            {
              path: '/dashboard/tasks',
              element: (
                <RoleBasedGuard accessibleRoles={homeowner()}>
                  <Tasks />{' '}
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'admin',
          children: [
            { path: '/dashboard/admin', element: <Navigate to="/dashboard/admin/role" replace /> },
            {
              path: 'broadcasts',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <BroadcastAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'role',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <RoleAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'role/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <RoleAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'brands',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterBrandAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'brands/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterBrandAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'brands/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterBrandAction />{' '}
                </RoleBasedGuard>
              )
            },
            {
              path: 'appliance-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterApplianceTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'appliance-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterApplianceTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'appliance-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterApplianceTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'industry-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterIndustryTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'industry-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterIndustryTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'industry-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterIndustryTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'items',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <ItemAdmin />
                </RoleBasedGuard>
              )
            },
            {
              // Home Care pick/pack (card 220). Manual fulfillment for the first
              // cohort is the design, not a stopgap: this screen is how you find
              // out whether the derived box contents are right, and a 3PL built
              // on contents nobody read automates the wrong box.
              path: 'care-fulfillment',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <CareFulfillment />
                </RoleBasedGuard>
              )
            },
            {
              // Company claim review (Service Pro card 69). The manual rung of
              // the ownership ladder — every automated check needs a website
              // domain, and plenty of real trades do not have one. Approving
              // here CREATES a company and a login, so it is admin-only on the
              // server too (middlewares/require-admin.js), not just here.
              path: 'company-claims',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <ProClaimReview />
                </RoleBasedGuard>
              )
            },
            {
              // The end-to-end funnel report (card 72) — the answer to whether
              // a campaign that is not converting has a bad ad or a bad offer.
              path: 'footprint-funnel',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <ProFunnel />
                </RoleBasedGuard>
              )
            },
            {
              path: 'items/new',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <EditItem />
                </RoleBasedGuard>
              )
            },
            {
              path: 'items/:id',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <EditItem />
                </RoleBasedGuard>
              )
            },
            {
              path: 'items/:itemId/tasks/new',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <DashboardAdminItemsItemIdTasksNew />
                </RoleBasedGuard>
              )
            },
            {
              path: 'exterior-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterExteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'exterior-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterExteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'exterior-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterExteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'utilities-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterUtilitiesTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'utilities-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterUtilitiesTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'utilities-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterUtilitiesTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'home-hacks',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <HomeHacksAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'home-hacks/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <HomeHacksAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'home-hacks/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <HomeHacksAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lawnLeisure-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterLawnTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lawnLeisure-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterLawnTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'lawnLeisure-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterLawnTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'furniture-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterFurnitureTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'furniture-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterFurnitureTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'furniture-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterFurnitureTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'interior-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterInteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'interior-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterInteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'interior-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterInteriorTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'email-notification',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterSendEmailNotificationAction />
                </RoleBasedGuard>
              )
            },
            { path: 'blog', element: <BlogAction /> },
            { path: 'blog/create', element: <BlogAction /> },
            { path: 'blog/edit', element: <BlogAction /> },
            { path: 'news', element: <NewsAction /> },
            { path: 'news/create', element: <NewsAction /> },
            { path: 'news/edit', element: <NewsAction /> },
            { path: 'coming-soon', element: <ComingSoonPostAction /> },
            { path: 'coming-soon/create', element: <ComingSoonPostAction /> },
            { path: 'coming-soon/edit', element: <ComingSoonPostAction /> }
          ]
        },
        {
          path: 'user',
          children: [
            { path: '/dashboard/user', element: <Navigate to="/dashboard/user/profile" replace /> },
            { path: 'profile', element: <UserProfile /> },
            // `cards`, `list`, `new` and `:name/edit` are GONE (2026-08-31).
            //
            // Minimal-template scaffolding that listed and edited EVERY user in
            // the system, sitting in the plain `user` section next to profile and
            // account with no RoleBasedGuard on them. Nothing linked to them, so
            // they were reachable only by typing the URL — but the API behind
            // them was open too until the /users routes were guarded, and that
            // combination is how a homeowner ends up reading everyone's email.
            //
            // Not gated, deleted: the real admin user list is
            // pages/dashboard/admin/UserListing, which is already behind
            // `RoleBasedGuard onlyAdminAccess`. These were a duplicate of it.
            { path: 'account', element: <UserAccount /> }
          ]
        }
      ]
    },
    {
      path: 'service-pro',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '/service-pro', element: <Navigate to="dashboard" replace /> },
        {
          path: 'dashboard',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <ServiceProDashboard />
            </RoleBasedGuard>
          )
        },
        {
          path: 'dispatch',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <DispatchBoard />
            </RoleBasedGuard>
          )
        },
        {
          // The crew app screen for a Seasonal Care Visit (card 221). Reached
          // from the dispatch board rather than being its own menu item: a tech
          // opens the job they are standing outside, not a list of every visit.
          path: 'care-visits/:jobId',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CareVisit />
            </RoleBasedGuard>
          )
        },
        {
          path: 'plans',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <ServicePlans />
            </RoleBasedGuard>
          )
        },
        {
          path: 'territories',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <Territories />
            </RoleBasedGuard>
          )
        },
        {
          path: 'trial',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <Trial />
            </RoleBasedGuard>
          )
        },
        {
          path: 'invoices',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <ProInvoices />
            </RoleBasedGuard>
          )
        },
        {
          path: 'crm',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CrmIntegrations />
            </RoleBasedGuard>
          )
        },
        // Where each CRM redirects back to. The code is exchanged from here with
        // the user's own session, never on an open API endpoint.
        {
          path: 'crm/callback/:provider',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CrmCallback />
            </RoleBasedGuard>
          )
        },
        {
          path: 'marketing',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <Marketing />
            </RoleBasedGuard>
          )
        },
        {
          path: 'campaigns',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <Campaigns />
            </RoleBasedGuard>
          )
        },
        {
          path: 'address-search',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <AddressSearch />
            </RoleBasedGuard>
          )
        },
        {
          path: 'customers',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CustomerAction />
            </RoleBasedGuard>
          )
        },
        {
          path: 'customers/create',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CustomerAction />
            </RoleBasedGuard>
          )
        },
        {
          path: 'customers/edit',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CustomerAction />
            </RoleBasedGuard>
          )
        },
        {
          path: 'customers/map',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <CustomersMap />
            </RoleBasedGuard>
          )
        },
        {
          path: 'inspections',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <InspectionAction />
            </RoleBasedGuard>
          )
        },
        {
          path: 'inspections/:id',
          element: (
            <RoleBasedGuard accessibleRoles={serviceProAccess()}>
              <InspectionDetail />
            </RoleBasedGuard>
          )
        },
        {
          path: 'pro',
          children: [
            { path: '/service-pro/pro', element: <Navigate to="/company" replace /> },
            {
              path: 'company',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <CompanyAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <CompanyAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <CompanyAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'users',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <UserAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'users/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <UserAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'users/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <UserAction />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'admin',
          children: [
            { path: '/service-pro/admin', element: <Navigate to="/company-type" replace /> },
            {
              path: 'company-type',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterCompanyTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company-type/create',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterCompanyTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company-type/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <MasterCompanyTypeAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <AdminCompanyAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company/add',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <AdminCompanyAction />
                </RoleBasedGuard>
              )
            },
            {
              path: 'company/edit',
              element: (
                <RoleBasedGuard accessibleRoles={onlyAdminAccess}>
                  <AdminCompanyAction />
                </RoleBasedGuard>
              )
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        // Email footer one-click unsubscribe (card 147). Public on purpose — the
        // signed token in the path is the credential, so it works from an inbox.
        { path: 'unsubscribe/:token', element: <Unsubscribe /> },
        // Card 44 — a service company's MARKETING unsubscribe. Separate from the
        // line above on purpose: that one is "no more ResiView notification
        // email" and is keyed to a user; this is "this contractor may not email
        // me" and is keyed to one company. One URL for both would send campaign
        // links to a handler that cannot read their token.
        { path: 'campaign-unsubscribe/:token', element: <CampaignUnsubscribe /> },
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { path: '/', element: <LandingPage /> },
        // Home Care interest page (card 216). Public and unauthenticated — it is
        // the social-ad target, and requiring a login to express interest would
        // gut the top of the funnel.
        { path: 'care', element: <Care /> },
        // Free Digital Footprint Review (Service Pro epic 57). The Service Pro
        // side has no self-serve front door at all — a company becomes a Pro
        // only because we reach out — and this is the organic path in.
        //
        // BOTH ROUTES ARE PUBLIC. The report is reached by magic link with no
        // account: a contractor opens marketing email on a Sunday and must not
        // hit a signup wall to read a report about their own business.
        { path: 'pro-audit', element: <ProAudit /> },
        { path: 'pro-audit/:token', element: <ProAuditReport /> },
        // Its own unsubscribe scope (card 68) — not card 147's account-level
        // switch and not card 44's per-company one. Declared after the report
        // route; the two-segment path cannot be caught by :token.
        { path: 'pro-audit/:token/unsubscribe', element: <ProAuditUnsubscribe /> },
        // Claim your company (card 69) — the conversion step. Also public: the
        // claimant has no account yet, and requiring one before they can prove
        // they own the business is the circular ask that ends the funnel.
        { path: 'pro-audit/:token/claim', element: <ProAuditClaim /> },
        { path: 'home', element: <About /> },
        { path: 'about-us', element: <About /> },
        { path: 'faqs', element: <Faqs /> },
        { path: 'terms', element: <Terms /> },
        { path: 'privacy', element: <Privacy /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}
